(() => {
  const body = document.body;
  const root = window.Shopify?.routes?.root || '/';
  const i18n = window.BcosShopTools || {};

  const WISH_KEY = 'bcos:wishlist';
  const COMPARE_KEY = 'bcos:compare';
  const MAX_COMPARE = 4;

  const tools = document.querySelector('[data-bcos-shop-tools]');
  if (!tools) return;

  const quickViewEl = tools.querySelector('[data-bcos-qv]');
  const quickViewBody = tools.querySelector('[data-bcos-qv-body]');
  const wishlistEl = tools.querySelector('[data-bcos-wishlist-drawer]');
  const wishlistItems = tools.querySelector('[data-bcos-wishlist-items]');
  const wishlistEmpty = tools.querySelector('[data-bcos-wishlist-empty]');
  const compareBar = tools.querySelector('[data-bcos-compare-bar]');
  const compareBarItems = tools.querySelector('[data-bcos-compare-bar-items]');
  const compareEl = tools.querySelector('[data-bcos-compare-modal]');
  const compareBody = tools.querySelector('[data-bcos-compare-body]');

  const overlays = new WeakMap();
  let lastFocused = null;

  function isReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function getFocusable(container) {
    return [...container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )].filter((el) => el.offsetParent !== null || el === document.activeElement);
  }

  function trapFocus(container, event) {
    const focusable = getFocusable(container);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function openOverlay(el, focusTarget) {
    el.hidden = false;
    requestAnimationFrame(() => el.setAttribute('data-open', 'true'));
    body.classList.add('bcos-scroll-lock');
    if (focusTarget && focusTarget.type !== 'hidden') focusTarget.focus();
    else if (el.querySelector('button')) el.querySelector('button').focus();
    const keyHandler = (event) => {
      if (event.key === 'Escape') closeOverlay(el);
      if (event.key === 'Tab') trapFocus(el, event);
    };
    document.addEventListener('keydown', keyHandler);
    el._bcosKeyHandler = keyHandler;
  }

  function closeOverlay(el) {
    if (el.getAttribute('data-open') !== 'true') return;
    el.setAttribute('data-open', 'false');
    document.removeEventListener('keydown', el._bcosKeyHandler);
    setTimeout(() => {
      el.hidden = true;
    }, isReducedMotion() ? 0 : 280);
    body.classList.remove('bcos-scroll-lock');
    if (lastFocused && lastFocused.isConnected) lastFocused.focus();
  }

  function formatMoney(cents) {
    return window.Shopify?.formatMoney
      ? window.Shopify.formatMoney(cents, window.Shopify?.moneyFormat)
      : `$${(cents / 100).toFixed(2)}`;
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (ch) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    })[ch]);
  }

  function readList(key) {
    try {
      return JSON.parse(localStorage.getItem(key)) || [];
    } catch (error) {
      return [];
    }
  }

  function sanitizeList(key) {
    const clean = readList(key).filter((item) => item && String(item.id) && String(item.id) !== 'undefined' && item.title && item.title !== 'undefined');
    if (clean.length !== readList(key).length) writeList(key, clean);
  }

  function writeList(key, list) {
    localStorage.setItem(key, JSON.stringify(list));
  }

  /* ---------------------------------------------------------- */
  /* Toast notifications                                         */
  /* ---------------------------------------------------------- */
  let toastEl = null;
  let toastTimer = null;

  function ensureToast() {
    if (toastEl) return toastEl;
    toastEl = document.createElement('div');
    toastEl.className = 'bcos-toast';
    toastEl.setAttribute('data-bcos-toast', '');
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    document.body.appendChild(toastEl);
    return toastEl;
  }

  function showToast(message) {
    const el = ensureToast();
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      el.classList.remove('is-visible');
    }, 2400);
  }

  /* ---------------------------------------------------------- */
  /* Quick add to cart (reuses the global AJAX handler)          */
  /* ---------------------------------------------------------- */
  async function quickAdd(variantId) {
    const form = document.createElement('form');
    form.setAttribute('data-bcos-add-to-cart', '');
    form.hidden = true;
    const idInput = document.createElement('input');
    idInput.type = 'hidden';
    idInput.name = 'id';
    idInput.value = variantId;
    const qtyInput = document.createElement('input');
    qtyInput.type = 'hidden';
    qtyInput.name = 'quantity';
    qtyInput.value = '1';
    form.append(idInput, qtyInput);
    document.body.appendChild(form);
    form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    form.remove();
  }

  /* ---------------------------------------------------------- */
  /* Quick view                                                  */
  /* ---------------------------------------------------------- */
  async function openQuickView(url, handle) {
    lastFocused = document.activeElement;
    openOverlay(quickViewEl);
    window.BcosAnalytics?.track('view_item', {
      items: [{ item_id: handle, item_name: handle }],
    }, 'analytics');
    quickViewBody.innerHTML = `<p class="bcos-qv__loading">${escapeHtml(i18n.quickView?.loading || 'Loading…')}</p>`;
    const productUrl = `${root}products/${encodeURIComponent(handle)}.js`;
    try {
      const res = await fetch(productUrl);
      if (!res.ok) throw new Error('product fetch failed');
      const product = await res.json();
      renderQuickView(product);
    } catch (error) {
      quickViewBody.innerHTML = `
        <p class="bcos-qv__error">${escapeHtml(i18n.quickView?.error || 'Unable to load product.')}</p>
        <a class="bcos-button bcos-button--small" href="${escapeHtml(url)}">${escapeHtml(i18n.quickView?.viewDetails || 'View full details')}</a>`;
    }
  }

  function renderQuickView(product) {
    const variants = product.variants || [];
    const single = variants.length === 1;
    const price = parseInt(product.price || '0', 10);
    const compareAt = parseInt(product.compare_at_price || '0', 10);
    const image = product.images?.[0]?.src || product.featured_image?.src || product.featured_image || '';
    const options = (product.options || []).map((option) => option.name).filter(Boolean);

    const variantSelect = single ? '' : `
      <label class="bcos-qv__field" for="bcos-qv-variant">
        <span class="bcos-qv__label">${escapeHtml(i18n.quickView?.options || 'Options')}</span>
        <select id="bcos-qv-variant" data-bcos-qv-variant>
          ${variants.map((variant) => {
            const label = [variant.title || ''].join(' ');
            return `<option value="${variant.id}" data-price="${variant.price}" data-compare-at="${variant.compare_at_price || ''}" data-available="${variant.available}" ${variant.id === product.variants?.[0]?.id ? 'selected' : ''}>${escapeHtml(label)}</option>`;
          }).join('')}
        </select>
      </label>`;

    const defaultVariant = variants[0] || {};

    quickViewBody.innerHTML = `
      <div class="bcos-qv__grid">
        <div class="bcos-qv__media">
          ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" width="480" height="600" loading="lazy">` : `<span class="bcos-qv__no-image" aria-hidden="true">${escapeHtml(i18n.products?.noImage || 'No image')}</span>`}
        </div>
        <div class="bcos-qv__info">
          ${options.length ? `<p class="bcos-qv__options">${escapeHtml(options.join(' · '))}</p>` : ''}
          <h3 class="bcos-qv__title">${escapeHtml(product.title)}</h3>
          <p class="bcos-qv__price">
            <span class="bcos-price" data-bcos-qv-price>${formatMoney(price)}</span>
            ${compareAt > price ? `<s class="bcos-price__compare" data-bcos-qv-compare>${formatMoney(compareAt)}</s>` : ''}
          </p>
          ${product.body_html ? `<div class="bcos-qv__description">${product.body_html}</div>` : ''}
          <form data-bcos-add-to-cart>
            <input type="hidden" name="id" value="${defaultVariant.id || ''}" data-bcos-qv-id>
            ${variantSelect}
            <div class="bcos-qv__qty" data-bcos-qv-qty>
              <button type="button" class="bcos-qv__qty-btn" data-bcos-qv-minus aria-label="${escapeHtml(i18n.quickView?.decrease || 'Decrease quantity')}">−</button>
              <input type="number" name="quantity" value="1" min="1" class="bcos-qv__qty-input" data-quantity-input aria-label="${escapeHtml(i18n.quickView?.quantity || 'Quantity')}">
              <button type="button" class="bcos-qv__qty-btn" data-bcos-qv-plus aria-label="${escapeHtml(i18n.quickView?.increase || 'Increase quantity')}">+</button>
            </div>
            <button class="bcos-button bcos-button--block" type="submit" data-bcos-qv-submit>
              ${escapeHtml(i18n.quickView?.addToCart || 'Add to cart')}
            </button>
          </form>
          <a class="bcos-qv__details-link" href="${escapeHtml(product.url)}">${escapeHtml(i18n.quickView?.viewDetails || 'View full details')}</a>
        </div>
      </div>`;

    const select = quickViewBody.querySelector('[data-bcos-qv-variant]');
    const idInput = quickViewBody.querySelector('[data-bcos-qv-id]');
    const priceEl = quickViewBody.querySelector('[data-bcos-qv-price]');
    const compareEl2 = quickViewBody.querySelector('[data-bcos-qv-compare]');
    const submit = quickViewBody.querySelector('[data-bcos-qv-submit]');

    if (select) {
      const updatePrice = () => {
        const option = select.selectedOptions[0];
        if (idInput) idInput.value = option.value;
        priceEl.textContent = formatMoney(parseInt(option.dataset.price || '0', 10));
        if (compareEl2) {
          const ca = parseInt(option.dataset.compareAt || '0', 10);
          compareEl2.textContent = ca > 0 ? formatMoney(ca) : '';
          compareEl2.style.display = ca > 0 ? '' : 'none';
        }
        if (submit) submit.disabled = option.dataset.available !== 'true';
      };
      select.addEventListener('change', updatePrice);
      updatePrice();
    } else if (submit && defaultVariant.available === false) {
      submit.disabled = true;
    }

    const qtyInput = quickViewBody.querySelector('[data-bcos-qv-qty] input[type="number"]');
    quickViewBody.querySelectorAll('[data-bcos-qv-minus], [data-bcos-qv-plus]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!qtyInput) return;
        const current = parseInt(qtyInput.value, 10) || 1;
        const next = btn.hasAttribute('data-bcos-qv-plus') ? current + 1 : Math.max(1, current - 1);
        qtyInput.value = next;
      });
    });
  }

  quickViewEl.querySelectorAll('[data-bcos-qv-close]').forEach((btn) => {
    btn.addEventListener('click', () => closeOverlay(quickViewEl));
  });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-bcos-quick-view]');
    if (!trigger) return;
    event.preventDefault();
    openQuickView(trigger.dataset.productUrl, trigger.dataset.productHandle);
  });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-bcos-quick-add]');
    if (!trigger) return;
    event.preventDefault();
    const variantId = trigger.dataset.variantId;
    const available = trigger.dataset.variantAvailable === 'true';
    if (!variantId || !available) return;
    quickAdd(variantId);
  });

  /* ---------------------------------------------------------- */
  /* Wishlist (localStorage)                                     */
  /* ---------------------------------------------------------- */
  function readWishlist() {
    return readList(WISH_KEY);
  }

  function inWishlist(id) {
    return readWishlist().some((item) => String(item.id) === String(id));
  }

  function toggleWishlist(productData) {
    const list = readWishlist();
    const index = list.findIndex((item) => String(item.id) === String(productData.id));
    let added = false;
    if (index >= 0) list.splice(index, 1);
    else {
      list.push(productData);
      added = true;
    }
    writeList(WISH_KEY, list);
    syncWishlistButtons();
    renderWishlist();
    return added;
  }

  function syncWishlistCount() {
    const count = readWishlist().length;
    document.querySelectorAll('[data-bcos-wishlist-count]').forEach((el) => {
      el.hidden = count === 0;
      el.textContent = count;
    });
  }

  function syncWishlistButtons() {
    const list = readWishlist();
    document.querySelectorAll('[data-bcos-wishlist]').forEach((btn) => {
      const active = list.some((item) => String(item.id) === String(btn.dataset.productId));
      btn.setAttribute('aria-pressed', String(active));
      btn.classList.toggle('is-active', active);
    });
    syncWishlistCount();
  }

  function renderWishlist() {
    const list = readWishlist();
    wishlistEmpty.hidden = list.length > 0;
    if (!list.length) {
      wishlistItems.innerHTML = '';
      return;
    }
    wishlistItems.innerHTML = list.map((item) => `
      <div class="bcos-wishlist-item">
        <a class="bcos-wishlist-item__media" href="${escapeHtml(item.url)}">
          ${item.image ? `<img src="${escapeHtml(item.image)}" alt="" width="80" height="100" loading="lazy">` : `<span class="bcos-wishlist-item__no-image" aria-hidden="true"></span>`}
        </a>
        <div class="bcos-wishlist-item__info">
          <a class="bcos-wishlist-item__title" href="${escapeHtml(item.url)}">${escapeHtml(item.title)}</a>
          <p class="bcos-wishlist-item__price">${formatMoney(parseInt(item.price || '0', 10))}</p>
          <button class="bcos-button bcos-button--small" type="button" data-bcos-wishlist-add data-variant-id="${item.variantId || item.id}">
            ${escapeHtml(i18n.wishlist?.addToCart || 'Add to cart')}
          </button>
        </div>
        <button class="bcos-wishlist-item__remove" type="button" data-bcos-wishlist-remove data-product-id="${item.id}" aria-label="${escapeHtml(i18n.wishlist?.remove || 'Remove from wishlist')}">
          ${escapeHtml(i18n.wishlist?.remove || 'Remove')}
        </button>
      </div>
    `).join('');
  }

  wishlistEl.querySelectorAll('[data-bcos-wishlist-close]').forEach((btn) => {
    btn.addEventListener('click', () => closeOverlay(wishlistEl));
  });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-bcos-wishlist]');
    if (!trigger) return;
    event.preventDefault();
    lastFocused = document.activeElement;
    const added = toggleWishlist({
      id: trigger.dataset.productId,
      variantId: trigger.dataset.productVariantId,
      title: trigger.dataset.productTitle,
      price: trigger.dataset.productPrice,
      compareAt: trigger.dataset.productCompareAtPrice,
      image: trigger.dataset.productImage,
      url: trigger.dataset.productUrl,
      handle: trigger.dataset.productHandle,
    });
    showToast(added ? (i18n.wishlist?.added || 'Added to wishlist') : (i18n.wishlist?.removed || 'Removed from wishlist'));
    window.BcosAnalytics?.track(added ? 'add_to_wishlist' : 'remove_from_wishlist', {
      items: [{
        item_id: trigger.dataset.productId,
        item_name: trigger.dataset.productTitle,
      }],
    }, 'marketing');
  });

  document.addEventListener('click', (event) => {
    const open = event.target.closest('[data-bcos-wishlist-open]');
    if (open) {
      event.preventDefault();
      lastFocused = document.activeElement;
      renderWishlist();
      openOverlay(wishlistEl);
      return;
    }
    const remove = event.target.closest('[data-bcos-wishlist-remove]');
    if (remove) {
      const list = readWishlist().filter((item) => String(item.id) !== String(remove.dataset.productId));
      writeList(WISH_KEY, list);
      syncWishlistButtons();
      renderWishlist();
      return;
    }
    const add = event.target.closest('[data-bcos-wishlist-add]');
    if (add) quickAdd(add.dataset.variantId);
  });

  /* ---------------------------------------------------------- */
  /* Compare                                                     */
  /* ---------------------------------------------------------- */
  function readCompare() {
    return readList(COMPARE_KEY);
  }

  function inCompare(id) {
    return readCompare().some((item) => String(item.id) === String(id));
  }

  function toggleCompare(productData) {
    const list = readCompare();
    const index = list.findIndex((item) => String(item.id) === String(productData.id));
    let added = false;
    if (index >= 0) list.splice(index, 1);
    else {
      if (list.length >= MAX_COMPARE) {
        showToast(i18n.compare?.max || `Maximum ${MAX_COMPARE} products to compare`);
        return false;
      }
      list.push(productData);
      added = true;
    }
    writeList(COMPARE_KEY, list);
    syncCompareButtons();
    renderCompareBar();
    return added;
  }

  function syncCompareButtons() {
    const list = readCompare();
    document.querySelectorAll('[data-bcos-compare]').forEach((btn) => {
      const active = list.some((item) => String(item.id) === String(btn.dataset.productId));
      btn.setAttribute('aria-pressed', String(active));
      btn.classList.toggle('is-active', active);
    });
  }

  function renderCompareBar() {
    const list = readCompare();
    if (!list.length) {
      compareBar.hidden = true;
      compareBarItems.innerHTML = '';
      return;
    }
    compareBar.hidden = false;
    compareBarItems.innerHTML = list.map((item) => `
      <div class="bcos-compare-bar__item">
        ${item.image ? `<img src="${escapeHtml(item.image)}" alt="" width="56" height="70" loading="lazy">` : `<span class="bcos-compare-bar__no-image" aria-hidden="true"></span>`}
        <button class="bcos-compare-bar__remove" type="button" data-bcos-compare-remove data-product-id="${item.id}" aria-label="${escapeHtml(i18n.compare?.remove || 'Remove from compare')}">
          <span aria-hidden="true">×</span>
        </button>
      </div>
    `).join('');
  }

  async function openCompareModal() {
    const list = readCompare();
    lastFocused = document.activeElement;
    compareBody.innerHTML = `<p class="bcos-compare__loading">${escapeHtml(i18n.compare?.loading || 'Loading…')}</p>`;
    openOverlay(compareEl);

    const enriched = await Promise.all(list.map(async (item) => {
      try {
        const res = await fetch(`${root}products/${encodeURIComponent(item.handle)}.js`);
        if (!res.ok) return { ...item, description: '' };
        const data = await res.json();
        return { ...item, description: data.body_html || '' };
      } catch (error) {
        return { ...item, description: '' };
      }
    }));

    const cols = enriched.map((item) => `
      <th scope="col" class="bcos-compare__cell bcos-compare__cell--product">
        <a href="${escapeHtml(item.url)}">
          ${item.image ? `<img src="${escapeHtml(item.image)}" alt="" width="200" height="250" loading="lazy">` : `<span class="bcos-compare__no-image" aria-hidden="true">${escapeHtml(i18n.products?.noImage || 'No image')}</span>`}
          <span class="bcos-compare__name">${escapeHtml(item.title)}</span>
        </a>
      </th>
    `).join('');

    const row = (label, cells) => `
      <tr>
        <th scope="row" class="bcos-compare__label">${escapeHtml(label)}</th>
        ${cells}
      </tr>`;

    const priceCells = enriched.map((item) => {
      const price = parseInt(item.price || '0', 10);
      const compareAt = parseInt(item.compareAt || item.compare_at_price || '0', 10);
      return `<td class="bcos-compare__cell">${formatMoney(price)}${compareAt > price ? ` <s>${formatMoney(compareAt)}</s>` : ''}</td>`;
    }).join('');

    const actionCells = enriched.map((item) => `
      <td class="bcos-compare__cell">
        <button class="bcos-button bcos-button--small" type="button" data-bcos-compare-add data-variant-id="${item.variantId || item.id}">
          ${escapeHtml(i18n.compare?.addToCart || 'Add to cart')}
        </button>
      </td>
    `).join('');

    const linkCells = enriched.map((item) => `
      <td class="bcos-compare__cell"><a href="${escapeHtml(item.url)}">${escapeHtml(i18n.compare?.viewDetails || 'View details')}</a></td>
    `).join('');

    const descriptionCells = enriched.map((item) => `
      <td class="bcos-compare__cell">${item.description || ''}</td>
    `).join('');

    compareBody.innerHTML = `
      <div class="bcos-compare__table-wrap">
        <table class="bcos-compare__table">
          <thead><tr>${cols}</tr></thead>
          <tbody>
            ${row(i18n.compare?.price || 'Price', priceCells)}
            ${row(i18n.compare?.actions || '', actionCells)}
            ${row(i18n.compare?.link || '', linkCells)}
            ${row(i18n.compare?.description || 'Description', descriptionCells)}
          </tbody>
        </table>
      </div>`;
  }

  compareEl.querySelectorAll('[data-bcos-compare-close]').forEach((btn) => {
    btn.addEventListener('click', () => closeOverlay(compareEl));
  });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-bcos-compare]');
    if (!trigger) return;
    event.preventDefault();
    const added = toggleCompare({
      id: trigger.dataset.productId,
      variantId: trigger.dataset.productVariantId,
      title: trigger.dataset.productTitle,
      price: trigger.dataset.productPrice,
      compareAt: trigger.dataset.productCompareAtPrice,
      image: trigger.dataset.productImage,
      url: trigger.dataset.productUrl,
      handle: trigger.dataset.productHandle,
    });
    if (added) showToast(i18n.compare?.added || 'Added to compare');
    if (added) window.BcosAnalytics?.track('add_to_compare', {
      items: [{ item_id: trigger.dataset.productId, item_name: trigger.dataset.productTitle }],
    }, 'analytics');
  });

  document.addEventListener('click', (event) => {
    const open = event.target.closest('[data-bcos-compare-open]');
    if (open) {
      event.preventDefault();
      openCompareModal();
      return;
    }
    const clear = event.target.closest('[data-bcos-compare-clear]');
    if (clear) {
      writeList(COMPARE_KEY, []);
      syncCompareButtons();
      renderCompareBar();
      return;
    }
    const remove = event.target.closest('[data-bcos-compare-remove]');
    if (remove) {
      const list = readCompare().filter((item) => String(item.id) !== String(remove.dataset.productId));
      writeList(COMPARE_KEY, list);
      syncCompareButtons();
      renderCompareBar();
      return;
    }
    const add = event.target.closest('[data-bcos-compare-add]');
    if (add) quickAdd(add.dataset.variantId);
  });

  /* ---------------------------------------------------------- */
  /* Init                                                        */
  /* ---------------------------------------------------------- */
  sanitizeList(WISH_KEY);
  sanitizeList(COMPARE_KEY);
  syncWishlistButtons();
  renderWishlist();
  renderCompareBar();
})();
